<template>
  <div
    :class="[
      'add-new-popup z-[999] fixed transition-all inset-0 overflow-x-hidden overflow-y-auto py-4 sm:py-6 bg-black/60 backdrop-blur-md',
      { active: stateStoreInstance.active },
    ]"
  >
    <div class="popup-dialog flex transition-all max-w-2xl min-h-full items-center mx-auto px-4 sm:px-6 mt-8 sm:mt-0">
      <div class="w-full bg-white/95 backdrop-blur-lg rounded-2xl overflow-hidden relative shadow-2xl border border-white/20 mt-8 sm:mt-0">
        
        <!-- Header with Gradient Background -->
        <div class="bg-gradient-to-br from-pink-400 via-purple-400 to-blue-400 p-4 relative overflow-hidden">
          <div class="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
          <div class="relative flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-white/30 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <i class="material-symbols-outlined text-gray-900 !text-[20px]">person_add</i>
              </div>
              <div>
                <h5 class="text-lg font-bold text-gray-900">{{ directT('crm.leads.addNewLeadPopup.newLead') }}</h5>
                <p class="text-gray-700 text-xs">{{ directT('crm.leads.addNewLeadPopup.addNewLeadToSystem') }}</p>
              </div>
            </div>
            <button
              type="button"
              class="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-all duration-200 flex items-center justify-center"
              @click="stateStoreInstance.activeBtn"
            >
              <i class="material-symbols-outlined !text-[16px]">close</i>
            </button>
          </div>
        </div>
        
        <!-- Content Form -->
        <div class="bg-white p-4 overflow-y-auto max-h-[70vh]">
          <form @submit.prevent="saveLead" class="space-y-4">
            <!-- Compact Form Fields -->
            <div class="space-y-3">
              <!-- Name Fields Row -->
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ directT('crm.leads.addNewLeadPopup.firstName') }}</label>
                  <input
                    type="text"
                    v-model="newLead.name"
                    required
                    :placeholder="directT('crm.leads.addNewLeadPopup.firstName')"
                    class="w-full h-9 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 px-3 text-sm placeholder-gray-400 focus:border-purple-400 focus:ring-1 focus:ring-purple-400 focus:bg-white outline-none transition-all"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ directT('crm.leads.addNewLeadPopup.lastName') }}</label>
                  <input
                    type="text"
                    v-model="newLead.lastName"
                    :placeholder="directT('crm.leads.addNewLeadPopup.lastName')"
                    class="w-full h-9 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 px-3 text-sm placeholder-gray-400 focus:border-purple-400 focus:ring-1 focus:ring-purple-400 focus:bg-white outline-none transition-all"
                  />
                </div>
              </div>

              <!-- Email Field -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">{{ directT('crm.leads.addNewLeadPopup.emailAddress') }}</label>
                <div class="relative">
                  <i class="material-symbols-outlined absolute left-3 top-2 text-gray-400 !text-[16px]">email</i>
                  <input
                    type="email"
                    v-model="newLead.email"
                    required
                    placeholder="example@domain.com"
                    class="w-full h-9 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 px-3 pl-8 text-sm placeholder-gray-400 focus:border-purple-400 focus:ring-1 focus:ring-purple-400 focus:bg-white outline-none transition-all"
                  />
                </div>
              </div>

              <!-- Phone and Company Row -->
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ directT('crm.leads.addNewLeadPopup.phone') }}</label>
                  <input
                    type="tel"
                    v-model="newLead.phone"
                    :placeholder="directT('crm.leads.addNewLeadPopup.phonePlaceholder')"
                    class="w-full h-9 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 px-3 text-sm placeholder-gray-400 focus:border-purple-400 focus:ring-1 focus:ring-purple-400 focus:bg-white outline-none transition-all"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ directT('crm.leads.addNewLeadPopup.company') }}</label>
                  <input
                    type="text"
                    v-model="newLead.company"
                    :placeholder="directT('crm.leads.addNewLeadPopup.companyName')"
                    class="w-full h-9 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 px-3 text-sm placeholder-gray-400 focus:border-purple-400 focus:ring-1 focus:ring-purple-400 focus:bg-white outline-none transition-all"
                  />
                </div>
              </div>

              <!-- Source and Status Row -->
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ directT('crm.leads.addNewLeadPopup.leadSource') }}</label>
                  <select
                    v-model="newLead.source"
                    required
                    class="w-full h-9 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 px-3 text-sm focus:border-purple-400 focus:ring-1 focus:ring-purple-400 focus:bg-white outline-none transition-all"
                  >
                    <option value="" disabled>{{ directT('crm.leads.addNewLeadPopup.selectSource') }}</option>
                    <option value="Google">Google</option>
                    <option value="Facebook">Facebook</option>
                    <option value="LinkedIn">LinkedIn</option>
                    <option value="המלצה">{{ directT('crm.leads.addNewLeadPopup.referral') }}</option>
                    <option value="אתר אינטרנט">{{ directT('crm.leads.addNewLeadPopup.website') }}</option>
                    <option value="אחר">{{ directT('crm.leads.addNewLeadPopup.other') }}</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ directT('crm.leads.addNewLeadPopup.status') }}</label>
                  <select
                    v-model="newLead.status"
                    required
                    class="w-full h-9 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 px-3 text-sm focus:border-purple-400 focus:ring-1 focus:ring-purple-400 focus:bg-white outline-none transition-all"
                  >
                    <option value="" disabled>{{ directT('crm.leads.addNewLeadPopup.selectStatus') }}</option>
                    <option value="New">{{ directT('crm.leads.addNewLeadPopup.statusNew') }}</option>
                    <option value="Contacted">{{ directT('crm.leads.addNewLeadPopup.statusContacted') }}</option>
                    <option value="Qualified">{{ directT('crm.leads.addNewLeadPopup.statusQualified') }}</option>
                    <option value="Won">{{ directT('crm.leads.addNewLeadPopup.statusWon') }}</option>
                    <option value="Lost">{{ directT('crm.leads.addNewLeadPopup.statusLost') }}</option>
                  </select>
                </div>
              </div>

              <!-- Notes Field -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">{{ directT('crm.leads.addNewLeadPopup.notes') }}</label>
                <textarea
                  v-model="newLead.notes"
                  rows="2"
                  :placeholder="directT('crm.leads.addNewLeadPopup.additionalNotes')"
                  class="w-full rounded-lg bg-gray-50 border border-gray-200 text-gray-900 px-3 py-2 text-sm placeholder-gray-400 focus:border-purple-400 focus:ring-1 focus:ring-purple-400 focus:bg-white outline-none transition-all resize-none"
                ></textarea>
              </div>
            </div>
        
            <!-- Action Buttons -->
            <div class="flex justify-between gap-3 pt-4">
              <button
                type="button"
                class="py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-all duration-200 text-sm border border-gray-200"
                @click="stateStoreInstance.activeBtn"
              >
                {{ directT('crm.leads.addNewLeadPopup.cancel') }}
              </button>
              
              <button
                type="submit"
                :disabled="loading"
                class="py-2 px-6 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed text-sm shadow-sm"
              >
                <span v-if="loading">{{ directT('crm.leads.addNewLeadPopup.saving') }}</span>
                <span v-else>{{ directT('crm.leads.addNewLeadPopup.save') }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Toast Notification -->
  <div 
    v-if="toastMessage" 
    class="fixed bottom-6 right-6 left-6 md:left-auto md:max-w-md z-50"
  >
    <div class="p-4 rounded-2xl shadow-xl transform transition-all duration-300 border-2 backdrop-blur-lg animate-bounce"
         :class="{ 
           'bg-white/95 border-green-400 text-green-700': toastType === 'success', 
           'bg-white/95 border-red-400 text-red-700': toastType === 'error', 
           'bg-white/95 border-blue-400 text-blue-700': toastType === 'info' 
         }">
      <div class="flex items-center justify-between">
        <div class="flex items-center">
          <div class="w-10 h-10 rounded-xl flex items-center justify-center mr-3"
               :class="{ 
                 'bg-green-50 border border-green-200': toastType === 'success',
                 'bg-red-50 border border-red-200': toastType === 'error',
                 'bg-blue-50 border border-blue-200': toastType === 'info'
               }">
            <i class="material-symbols-outlined !text-[22px]" 
               v-if="toastType === 'success'">check_circle</i>
            <i class="material-symbols-outlined !text-[22px]" 
               v-else-if="toastType === 'error'">error</i>
            <i class="material-symbols-outlined !text-[22px]" 
               v-else>info</i>
          </div>
          <span class="font-semibold text-gray-800">{{ toastMessage }}</span>
        </div>
        <button @click="closeToast" class="ml-4 p-2 rounded-xl hover:bg-gray-100 transition-all duration-200">
          <i class="material-symbols-outlined !text-[20px] text-gray-500">close</i>
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch } from "vue";
import stateStore from "@/utils/store";
import { supabase } from "@/lib/supabaseClient";
import { getCurrentOrgId } from "@/utils/getCurrentOrgId";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

// Type definition for nested translation objects
type TranslationObject = { [key: string]: string | TranslationObject };

// Define interfaces
interface Status {
  id?: string;
  name: string;
  color: string;
}

interface Source {
  id?: string;
  name: string;
}

interface LeadForm {
  name: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  source: string;
  status: string;
  notes: string;
  priority: string;
  budget: number | string;
  nextFollowUp: string;
  preferredContact: string;
  successChance: number | string;
}

interface CustomField {
  key: string;
  label: string;
  type: string;
  required: boolean;
  options?: string[];
}

export default defineComponent({
  name: "AddNewLeadPopup",
  setup() {
    const stateStoreInstance = stateStore;
    
    // i18n setup
    const { locale } = useI18n();
    
    // Store direct references to translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return typeof result === 'string' ? result : key;
    };
    
    const loading = ref(false);
    const statuses = ref<Status[]>([]);
    const sources = ref<Source[]>([]);
    const organizationId = ref<string | null>(null);
    
    // Form data
    const newLead = ref<LeadForm>({
      name: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      source: '',
      status: '',
      notes: '',
      priority: '',
      budget: '',
      nextFollowUp: '',
      preferredContact: '',
      successChance: ''
    });
    
    // Custom fields
    const customFields = ref<CustomField[]>([]);
    const customFieldValues = ref<Record<string, string>>({});
    const loadingCustomFields = ref(false);
    
    // Toast notification
    const toastMessage = ref('');
    const toastType = ref<'success' | 'error' | 'info'>('info');
    const toastTimeout = ref<number | null>(null);
    
    // Clear form when modal is closed
    const clearForm = () => {
      newLead.value = {
        name: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        source: '',
        status: '',
        notes: '',
        priority: '',
        budget: '',
        nextFollowUp: '',
        preferredContact: '',
        successChance: ''
      };
      
      // Clear custom field values
      customFieldValues.value = {};
    };
    
    // Watch for changes in the modal's active state
    watch(() => stateStoreInstance.active, (isActive) => {
      if (!isActive) {
        clearForm();
      }
    });
    
    // Get appropriate input type based on custom field type
    const getInputType = (fieldType: string) => {
      switch (fieldType) {
        case 'number': return 'number';
        case 'email': return 'email';
        case 'phone': return 'tel';
        case 'date': return 'date';
        case 'checkbox': return 'checkbox';
        case 'select': return 'select';
        default: return 'text';
      }
    };
    
    // Fetch custom fields
    const fetchCustomFields = async () => {
      try {
        if (!organizationId.value) {
          console.warn('Cannot fetch custom fields: Missing organization ID');
          return;
        }
        
        loadingCustomFields.value = true;
        
        // Get custom fields definition
        const { data, error } = await supabase
          .from('customleadsfield')
          .select('id, content')
          .eq('organizationid', organizationId.value);
        
        if (error) throw error;
        
        if (!data || data.length === 0) {
          // No custom fields defined
          customFields.value = [];
          return;
        }
        
        // Reset the custom fields array
        customFields.value = [];
        
        // Process all records
        for (const record of data) {
          if (record && record.content) {
            // Add type assertion to content
            const content = record.content as any[];
            
            if (Array.isArray(content) && content.length > 0) {
              // Process each field in the content array
              for (const field of content) {
                if (field) {
                  (field as any).customFieldsId = record.id;
                  customFields.value.push(field as CustomField);
                  
                  // Initialize field value
                  customFieldValues.value[field.key] = '';
                }
              }
            }
          }
        }
        
        console.log('Custom fields loaded:', customFields.value);
      } catch (error) {
        console.error('Error fetching custom fields:', error);
        createToast(directT('crm.leads.addNewLeadPopup.failedToLoadCustomFields'), 'error');
      } finally {
        loadingCustomFields.value = false;
      }
    };
    
    // Save custom field values after creating the lead
    const saveCustomFieldValues = async (leadId: string) => {
      try {
        if (!leadId || customFields.value.length === 0) return;
        
        // Loop through all custom fields and save their values
        for (const field of customFields.value) {
          const value = customFieldValues.value[field.key];
          const customFieldsId = (field as any).customFieldsId;
          
          if (!customFieldsId) {
            console.error('Missing customFieldsId for field:', field.key);
            continue;
          }
          
          // Only save if there's a value
          if (value !== undefined && value !== null && value !== '') {
            const { error: insertError } = await supabase
              .from('customleadsfieldcontent')
              .insert({
                leadid: leadId,
                customleadsfieldid: customFieldsId,
                fieldkey: field.key,
                content: value,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              });
              
            if (insertError) {
              console.error('Error inserting custom field:', insertError);
            }
          }
        }
      } catch (error) {
        console.error('Error saving custom field values:', error);
      }
    };
    
    // Fetch status and source options
    const fetchOptions = async () => {
      try {
        organizationId.value = await getCurrentOrgId();
        
        if (!organizationId.value) {
          createToast(directT('crm.leads.addNewLeadPopup.userNotAssociatedWithOrganization'), 'error');
          return;
        }
        
        // Fetch statuses
        const { data: statusData, error: statusError } = await supabase
          .from('statusleads')
          .select('*')
          .eq('organization_id', organizationId.value);
        
        if (statusError) throw statusError;
        
        statuses.value = (statusData || []).map((item: any) => ({
          id: item.id,
          name: item.status,
          color: item.color || '#605DFF'
        }));
        

        
        // Fetch sources
        const { data: sourceData, error: sourceError } = await supabase
          .from('leadsources')
          .select('*')
          .eq('organization_id', organizationId.value);
        
        if (sourceError) throw sourceError;
        
        sources.value = (sourceData || []).map((item: any) => ({
          id: item.id,
          name: item.source
        }));
        
        // Fetch custom fields
        await fetchCustomFields();
      } catch (error) {
        console.error('Error fetching options:', error);
        createToast(directT('crm.leads.addNewLeadPopup.failedToFetchOptions'), 'error');
      }
    };
    
    // Save new lead
    const saveLead = async () => {
      loading.value = true;
      
      try {
        // Create lead data object
        const leadData = {
          name: newLead.value.name,
          email: newLead.value.email,
          phone: newLead.value.phone,
          status: newLead.value.status,
          source: newLead.value.source,
          notes: newLead.value.notes,
          data: { company: newLead.value.company },
          org_id: organizationId.value,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        // Insert to database
        const { data, error } = await supabase
          .from('leads')
          .insert(leadData)
          .select();
        
        if (error) throw error;
        
        // Save custom field values if we have a lead ID
        if (data && data.length > 0) {
          await saveCustomFieldValues(data[0].id as string);
        }
        
        createToast(directT('crm.leads.addNewLeadPopup.leadCreatedSuccessfully'), 'success');
        clearForm();
        stateStoreInstance.activeBtn(); // Close modal
        
        // Refresh leads list
        window.dispatchEvent(new CustomEvent('refreshLeads'));
      } catch (error) {
        console.error('Error creating lead:', error);
        createToast(directT('crm.leads.addNewLeadPopup.failedToCreateLead'), 'error');
      } finally {
        loading.value = false;
      }
    };
    
    // Toast functions
    const createToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
      toastMessage.value = message;
      toastType.value = type;
      
      if (toastTimeout.value) {
        clearTimeout(toastTimeout.value);
      }
      
      toastTimeout.value = window.setTimeout(() => {
        closeToast();
      }, 3000);
    };
    
    const closeToast = () => {
      toastMessage.value = '';
      if (toastTimeout.value) {
        clearTimeout(toastTimeout.value);
        toastTimeout.value = null;
      }
    };
    
    // Fetch options on component mount
    onMounted(async () => {
      await fetchOptions();
    });

    return {
      stateStoreInstance,
      loading,
      newLead,
      statuses,
      sources,
      saveLead,
      toastMessage,
      toastType,
      closeToast,
      customFields,
      customFieldValues,
      getInputType,
      directT
    };
  },
});
</script>
